import { type VariantProp, type VariantProps, cv } from "@suse-o11y/class-variance";
import React from "react";

import { ZIndexBoundary } from "~/components/z-index-boundary";

import styles from "./_dropdown-menu.module.css";

const LazyPortal = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Portal } = await import("@radix-ui/react-dropdown-menu");

	return { default: Portal };
});

const LazyContent = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Content } = await import("@radix-ui/react-dropdown-menu");

	return { default: Content };
});

const variants = cv(styles.content, {
	variants: {
		width: {
			auto: null,
			"trigger-max": styles.widthMax,
			"trigger-min": styles.widthMin,
			"trigger-same": styles.widthSame,
		},
	},

	defaultVariants: {
		width: "auto",
	},
});

/**
 * The width of the tooltip content.
 * - `auto` (default): The width is determined by the content.
 * - `trigger-max`: The width is at most the width of the trigger element.
 * - `trigger-min`: The width is at least the width of the trigger element.
 * - `trigger-same`: The width matches the width of the trigger element.
 */
export type DropdownMenuContentWidth = VariantProp<typeof variants, "width">;

export interface DropdownMenuContentProps
	extends VariantProps<typeof variants>,
		React.ComponentProps<typeof LazyContent> {}

export const DropdownMenuContent = React.forwardRef<React.ComponentRef<typeof LazyContent>, DropdownMenuContentProps>(
	({ className, width, style, ...props }, ref) => (
		<React.Suspense fallback={null}>
			<LazyPortal>
				<ZIndexBoundary>
					{(zIndex = 50) => (
						<LazyContent
							arrowPadding={4}
							className={variants({ width, className })}
							collisionPadding={8} // avoid sticking to the edges of the boundaries
							ref={ref} // avoid rendering the arrow on the rounded corners
							sideOffset={4} // distance from the trigger
							style={{ zIndex: zIndex + 1, ...style }}
							{...props}
						/>
					)}
				</ZIndexBoundary>
			</LazyPortal>
		</React.Suspense>
	),
);

if (import.meta.env.DEV) {
	DropdownMenuContent.displayName = "DropdownMenuContent";
}
